import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';

/** provider/tool of an account-wide precondition: checked before every action that needs approval
 * (and any low-risk action with its own preconditions), together with that action's own. */
export const GLOBAL_PRECONDITION_SCOPE = '*';

/** Which actions Brahma checks account-wide rules for (react.py's is_write_action): any action
 * that needs approval, plus a low-risk one only once it has enabled preconditions of its own. */
export const ACCOUNT_WIDE_SCOPE =
	"Checked, together with an action's own preconditions, before any action that needs approval (medium or high risk) runs automatically. Low-risk actions are checked only once they have preconditions of their own.";

export type PreconditionSource = 'input' | 'customer' | 'order';
export type PreconditionOperator = 'eq' | 'neq' | 'lt' | 'lte' | 'gt' | 'gte' | 'in' | 'exists';
export type PreconditionCombinator = 'all' | 'any';

export interface ToolPrecondition {
  id: string;
  provider: string;
  tool: string;
  combinator: PreconditionCombinator;
  source: PreconditionSource;
  sourceTool?: string;
  field: string;
  operator: PreconditionOperator;
  value?: unknown;
  description?: string;
  enabled: boolean;
}

export interface ToolPreconditionInput {
  provider: string;
  tool: string;
  combinator?: PreconditionCombinator;
  source: PreconditionSource;
  sourceTool?: string;
  field: string;
  operator: PreconditionOperator;
  value?: unknown;
  description?: string;
}

export const toolPreconditionService = {
  async list(provider: string, tool: string): Promise<ToolPrecondition[]> {
    const response = await api.get<ApiResponse<{ preconditions: ToolPrecondition[] }>>(
      API_ENDPOINTS.TOOL_PRECONDITIONS.BASE,
      { params: { provider, tool } },
    );
    return response.data.data.preconditions;
  },

  async create(data: ToolPreconditionInput): Promise<ToolPrecondition> {
    const response = await api.post<ApiResponse<{ precondition: ToolPrecondition }>>(
      API_ENDPOINTS.TOOL_PRECONDITIONS.BASE,
      data,
    );
    return response.data.data.precondition;
  },

  async update(id: string, data: Partial<ToolPreconditionInput> & { enabled?: boolean }): Promise<ToolPrecondition> {
    const response = await api.put<ApiResponse<{ precondition: ToolPrecondition }>>(
      API_ENDPOINTS.TOOL_PRECONDITIONS.BY_ID(id),
      data,
    );
    return response.data.data.precondition;
  },

  async remove(id: string): Promise<void> {
    await api.delete(API_ENDPOINTS.TOOL_PRECONDITIONS.BY_ID(id));
  },

  async setCombinator(provider: string, tool: string, combinator: PreconditionCombinator): Promise<void> {
    await api.patch(API_ENDPOINTS.TOOL_PRECONDITIONS.COMBINATOR, { provider, tool, combinator });
  },
};
